import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown, RotateCw, Search, X } from "lucide-react";
import { useState, type FormEvent } from "react";

import type { FilterStatus } from "@/api/admin";

const STATUS_OPTIONS: { value: FilterStatus; label: string }[] = [
  { value: "Pending", label: "Pending" },
  { value: "Acknowledged", label: "Acknowledged" },
  { value: "Attachments", label: "Has attachment" },
];

export function FilterBar({
  status,
  q,
  onChange,
  onRefresh,
  isRefetching,
}: {
  status: FilterStatus[];
  q: string;
  onChange: (next: { status: FilterStatus[]; q: string }) => void;
  onRefresh: () => void;
  isRefetching: boolean;
}) {
  const [draftQ, setDraftQ] = useState(q);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    onChange({ status, q: draftQ });
  }

  function setStatus(next: FilterStatus[]) {
    onChange({ status: next, q });
  }

  return (
    <form onSubmit={onSubmit} className="card p-3 flex flex-col sm:flex-row gap-2 sm:items-center">
      <div className="w-48">
        <Listbox value={status} onChange={setStatus} multiple>
          <div className="relative">
            <ListboxButton className="input-field flex items-center justify-between text-left">
              <span className="truncate text-sm">
                {status.length === 0 ? (
                  <span className="text-slate-400 dark:text-slate-500">Filter by status…</span>
                ) : (
                  status.join(", ")
                )}
              </span>
              <ChevronDown size={16} className="text-slate-400 ml-2 shrink-0" aria-hidden />
            </ListboxButton>
            <ListboxOptions
              className="z-10 mt-1 w-[var(--button-width)] overflow-auto rounded-md border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised shadow-lg max-h-60"
              anchor="bottom start"
            >
              {STATUS_OPTIONS.map((o) => (
                <ListboxOption
                  key={o.value}
                  value={o.value}
                  className="group flex items-center gap-2 cursor-pointer px-3 py-2 text-sm data-[focus]:bg-slate-100 dark:data-[focus]:bg-surface-border"
                >
                  {({ selected }) => (
                    <>
                      <span className="w-4 h-4 inline-flex items-center justify-center rounded border border-slate-300 dark:border-surface-border">
                        {selected && <Check size={12} className="text-brand-greenAlt" aria-hidden />}
                      </span>
                      {o.label}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>

      <div className="flex-1 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          type="text"
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
          placeholder="Search by App ID, student name, admin name, keyword, or date (e.g., 8 aug, august, 2025)…"
          className="input-field pl-9 pr-9"
          aria-label="Search tickets"
        />
        {draftQ && (
          <button
            type="button"
            onClick={() => {
              setDraftQ("");
              onChange({ status, q: "" });
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <button type="submit" className="btn-primary text-sm" disabled={isRefetching}>
          Search
        </button>
        <button type="button" onClick={onRefresh} className="btn-secondary text-sm" disabled={isRefetching}>
          <RotateCw size={14} className={isRefetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
    </form>
  );
}
